import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { Form } from "antd";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledFlex, StyledGridCol, StyledGridRow, StyledInformationList } from "@/common/styles";
import { BookingsSectionTitle } from "../BookingsDetails/styles";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import { useSelector } from "react-redux";

const PropertyChainCustodyForm = forwardRef(({ formData, onSave }, ref) => {
  const [form] = Form.useForm();
  const { bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );

  // When formData is provided (for editing), prefill the form;
  // otherwise, set default values for the hidden fields.
  useEffect(() => {
    if (formData && formData.length > 0) {    
      form.setFieldsValue({
        inventoryItemTransactionId: formData?.[0]?.inventoryItemTransactionId || null,
        dateAdded: formData?.[0]?.dateAdded || null,
        timeAdded: formData?.[0]?.timeAdded || null,
        user: formData?.[0]?.user || "System",
        transactionType: formData?.[0]?.transactionType || "Initial Entry",
        comments: formData?.[0]?.comments || "",
        courtOrder: formData?.[0]?.inventoryItemTransactionsExtras?.[0]?.courtOrder || "",
        judge: formData?.[0]?.inventoryItemTransactionsExtras?.[0]?.judge || "",
        issueDate: formData?.[0]?.inventoryItemTransactionsExtras?.[0]?.issueDate? dayjs(formData?.[0]?.inventoryItemTransactionsExtras?.[0]?.issueDate) : null,
        crimeLabResults: formData?.[0]?.inventoryItemTransactionsExtras?.[0]?.crimeLabResults || "",
        crimeLabTechnician: formData?.[0]?.inventoryItemTransactionsExtras?.[0]?.crimeLabTechnician || "",
      });
    } else {
      // For new transactions, set defaults for the hidden fields.
      form.setFieldsValue({
        inventoryItemTransactionId: null,
        dateAdded: null,
        timeAdded: null,
        user: "System",
        transactionType: "Initial Entry",
      });
    }
  }, [formData, form]);

  // Expose a save method to the parent that validates and returns transformed form values.
  useImperativeHandle(ref, () => ({
    save: async () => {
      const values = await form.validateFields();

      // Build the agency-specific extra payload.
      const extras = {
        inventoryItemTransactionExtraID: null,
        inventoryItemTransactionID: values.inventoryItemTransactionId,
        courtOrder: values.courtOrder,
        judge: values.judge,
        issueDate: values.issueDate? dayjs(values.issueDate).format("MM/DD/YYYY"): null,
        crimeLabResults: values.crimeLabResults,
        crimeLabTechnician: values.crimeLabTechnician,
      };

      // Construct the final payload that nests the extra info.
      const result = {
        inventoryItemTransactionId: values.inventoryItemTransactionId,
        dateAdded: values.dateAdded,
        timeAdded: values.timeAdded,
        user: values.user,
        transactionType: values.transactionType,
        comments: values.comments,
        inventoryItemTransactionsExtras: [extras],
      };
      return result;
    },
  }));

  return (
    <>
      <BookingsSectionTitle style={{ marginBottom: "10px", marginTop: "0px" }}>
        <span>Transaction Details</span>
      </BookingsSectionTitle>
      <StyledInformationList style={{ padding: "0px" }}>
        <StyledGridRow gutter={16} justify="space-between">
          <StyledGridCol>
            <StyledFlex>
              <label className="key" style={{ paddingRight: "5px" }}>
                Entered By:
              </label>
              <div className="value">
                {formData?.[0]?.user || "System"}
              </div>
            </StyledFlex>
            <StyledFlex>
              <label className="key" style={{ paddingRight: "5px" }}>
                Transaction Type:
              </label>
              <div className="value">
                {formData?.[0]?.transactionType || "Initial Entry"}
              </div>
            </StyledFlex>
          </StyledGridCol>
          <StyledGridCol>
            <StyledFlex>
              <label className="key" style={{ paddingRight: "5px" }}>
                Date:
              </label>
              <div className="value">
                {formData?.[0]?.dateAdded || dayjs().format("MM/DD/YYYY")}
              </div>
            </StyledFlex>
            <StyledFlex>
              <label className="key" style={{ paddingRight: "5px" }}>
                Time:
              </label>
              <div className="value">
                {formData?.[0]?.timeAdded || dayjs().format("HH:mm")}
              </div>
            </StyledFlex>
          </StyledGridCol>
        </StyledGridRow>
      </StyledInformationList>

      <Form form={form} layout="vertical">
        {/* Hidden fields */}
        <Form.Item name="inventoryItemTransactionId" hidden>
          <TextField />
        </Form.Item>
        <Form.Item name="dateAdded" hidden>
          <TextField />
        </Form.Item>
        <Form.Item name="timeAdded" hidden>
          <TextField />
        </Form.Item>
        <Form.Item name="user" hidden>
          <TextField />
        </Form.Item>
        <Form.Item name="transactionType" hidden>
          <TextField />
        </Form.Item>

        {/* Visible fields */}
        <StyledGridRow gutter={16}>
          <StyledGridCol lg={24}>
            <FormItem name="comments" label="Comments">
              <TextField inputType="textarea" rows="2" disabled={!bookingsFieldsEnabled} />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>

        <BookingsSectionTitle style={{ marginBottom: "10px", marginTop: "15px" }}>
          <span>Agency-Specific Information</span>
        </BookingsSectionTitle>
        <StyledGridRow gutter={16}>
          <StyledGridCol lg={8}>
            <FormItem label="Court Order #" name="courtOrder">
              <TextField disabled={!bookingsFieldsEnabled} />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Judge" name="judge">
              <TextField disabled={!bookingsFieldsEnabled} />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Issue Date" name="issueDate">
              <CommonDatePicker
                  placeholder="MM/DD/YYYY"
                  format="MM/DD/YYYY"
                  showTime={false}
                  disabled={!bookingsFieldsEnabled}
                />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Crime Lab Results" name="crimeLabResults">
              <TextField disabled={!bookingsFieldsEnabled} />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Crime Lab Technician" name="crimeLabTechnician">
              <TextField disabled={!bookingsFieldsEnabled} />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
      </Form>
    </>
  );
});

export default PropertyChainCustodyForm;
